import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { MatterGetOneV2ResponseDto } from '../../../../backend/src/matter/dto/matter-get-one-v2.dto';
import { MatterUpdateUsersRequestDto } from '../../../../backend/src/matter/dto/matter-update-users.request.dto';
import { QueryEntity } from '../types';
import { User } from '../types/backend-dto-types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export const useUpdateMatterUsers = (id: string | undefined) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const matterQueryKey = [QueryEntity.Matter, { matterId: id }];

  const updateMatterUsers = async (data: MatterUpdateUsersRequestDto) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.PATCH,
      data,
    });

    const response = await fetch(`${APIBaseChronos}/api/matters/${id}/users`, fetchConfig);
    if (!response.ok) {
      throw new Error('Error updating matter users');
    }
  };

  return useMutation({
    mutationFn: updateMatterUsers,
    onMutate: async ({ userIds }) => {
      await queryClient.cancelQueries({ queryKey: matterQueryKey });

      const previousMatter = queryClient.getQueryData<MatterGetOneV2ResponseDto>(matterQueryKey);
      if (!previousMatter) return { previousMatter };

      // Resolve the new id list to full user objects, preferring users already on the
      // matter, then falling back to the org users cache for newly added members.
      const orgUsers = queryClient.getQueryData<User[]>([QueryEntity.Users]) ?? [];
      const knownUsers = new Map<string, MatterGetOneV2ResponseDto['users'][number]>();
      for (const user of [...previousMatter.users, ...orgUsers]) {
        if (!knownUsers.has(user.id)) knownUsers.set(user.id, user);
      }

      const optimisticUsers = userIds
        .map((userId) => knownUsers.get(userId))
        .filter((user): user is MatterGetOneV2ResponseDto['users'][number] => user !== undefined);

      queryClient.setQueryData<MatterGetOneV2ResponseDto>(matterQueryKey, {
        ...previousMatter,
        users: optimisticUsers,
      });

      return { previousMatter };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousMatter) {
        queryClient.setQueryData(matterQueryKey, context.previousMatter);
      }
      console.error('Update matter users error:', error);
      toast.error(error.message || 'Failed to update matter users. Please try again.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: matterQueryKey });
    },
  });
};
